import { PAYMENT_METHODS, formatDate, type Payment } from '@fdt/shared';
import BlockIcon from '@mui/icons-material/Block';
import PaymentsIcon from '@mui/icons-material/Payments';
import {
  Autocomplete,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable, type Column } from '../components/DataTable';
import { ExportButton } from '../components/ExportButton';
import { FormDialog } from '../components/FormDialog';
import { MoneyText } from '../components/MoneyText';
import { useList } from '../hooks/useList';
import { useFormError } from '../hooks/useFormError';
import { currentLocale } from '../i18n';
import { customersApi } from '../resources';
import { paymentsApi } from '../resources/documents';

export function PaymentsPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [method, setMethod] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters = useMemo(
    () => ({
      customerId: customerId || undefined,
      method: method || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [customerId, method, from, to],
  );

  const crud = useList('payments', paymentsApi.list, { filters });

  const customers = useQuery({
    queryKey: ['customers', 'picker', customerSearch],
    queryFn: () =>
      customersApi.list({
        page: 1,
        pageSize: 50,
        search: customerSearch || undefined,
        sortBy: 'businessName',
      }),
  });

  const [cancelTarget, setCancelTarget] = useState<Payment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const cancelError = useFormError();
  const cancel = useMutation({
    mutationFn: () => paymentsApi.cancel(cancelTarget!.id, cancelReason),
    onSuccess: () => {
      setCancelTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['statement'] });
    },
    onError: (error) => cancelError.capture(error),
  });

  const columns: Column<Payment>[] = [
    {
      id: 'paidOn',
      label: t('fields.date'),
      render: (row) => formatDate(`${row.paidOn}T00:00:00Z`, locale),
    },
    {
      id: 'customerName',
      label: t('fields.businessName'),
      render: (row) => row.customerName,
    },
    {
      id: 'documentNumber',
      label: t('documents.number'),
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontVariantNumeric: 'tabular-nums', textDecoration: 'underline', cursor: 'pointer' }}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/documents/${row.documentId}`);
          }}
        >
          {row.documentNumber}
        </Typography>
      ),
    },
    {
      id: 'method',
      label: t('payments.method'),
      render: (row) => (
        <Chip size="small" variant="outlined" label={t(`payments.methods.${row.method}`)} />
      ),
    },
    {
      id: 'amount',
      label: t('documents.amount'),
      align: 'right',
      render: (row) => (
        <MoneyText
          value={row.amount}
          variant="body2"
          sx={{
            fontWeight: 600,
            textDecoration: row.cancelledAt ? 'line-through' : undefined,
            opacity: row.cancelledAt ? 0.6 : 1,
          }}
        />
      ),
    },
    {
      id: 'createdByUserName',
      label: t('payments.collectedBy'),
      render: (row) => row.createdByUserName ?? '—',
    },
    {
      id: 'status',
      label: t('fields.status'),
      render: (row) =>
        row.cancelledAt ? (
          <Chip size="small" color="error" variant="outlined" label={t('documents.cancelled')} />
        ) : (
          <Chip size="small" color="success" label={t('documents.active')} />
        ),
    },
    {
      id: 'actions',
      label: t('common.actions'),
      align: 'right',
      render: (row) =>
        row.cancelledAt ? null : (
          <Tooltip title={t('payments.cancel')}>
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                setCancelTarget(row);
                setCancelReason('');
                cancelError.clear();
              }}
              aria-label={t('payments.cancel')}
            >
              <BlockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <DataTable
        title={t('nav.payments')}
        icon={PaymentsIcon}
        tone="success"
        columns={columns}
        query={crud.list}
        search={crud.search}
        onSearchChange={crud.setSearch}
        sortBy={crud.sortBy}
        sortDir={crud.sortDir}
        onSort={crud.toggleSort}
        page={crud.page}
        onPageChange={crud.setPage}
        pageSize={crud.pageSize}
        onPageSizeChange={crud.setPageSize}
        rowKey={(row) => row.id}
        toolbar={
          <>
            <Autocomplete
              options={customers.data?.items ?? []}
              getOptionLabel={(option) => `${option.code} — ${option.businessName}`}
              filterOptions={(options) => options}
              onInputChange={(_, value) => setCustomerSearch(value)}
              onChange={(_, value) => setCustomerId(value?.id ?? '')}
              renderInput={(params) => (
                <TextField {...params} label={t('fields.businessName')} size="small" />
              )}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              label={t('payments.method')}
              value={method}
              onChange={(event) => setMethod(event.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {PAYMENT_METHODS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`payments.methods.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('routes.from')}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: 165 }}
            />
            <TextField
              label={t('routes.to')}
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: 165 }}
            />
            <ExportButton file="payments.xlsx" params={filters} />
          </>
        }
      />

      <FormDialog
        open={Boolean(cancelTarget)}
        title={t('payments.cancelTitle')}
        onClose={() => setCancelTarget(null)}
        onSubmit={() => cancel.mutate()}
        submitting={cancel.isPending}
        errorKey={cancelError.errorKey}
        submitLabel={t('payments.cancel')}
        maxWidth="xs"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {t('payments.cancelNotice', {
              amount: cancelTarget?.amount ?? '',
              document: cancelTarget?.documentNumber ?? '',
            })}
          </Typography>
          <TextField
            label={t('fields.reason')}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </FormDialog>
    </>
  );
}
